import React from "react";
import { commitMutation, graphql, type GraphQLTaggedNode, useLazyLoadQuery } from "react-relay";

import { faEllipsisV, faPowerOff, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import cx from "classnames";
import type { OperationType } from "relay-runtime";

import { AppContext } from "~/common";
import { confirm } from "~/lib/prompts";
import { environment } from "~/models";
import { Button, toast } from "~/ui";
import IconButton from "~/ui/button/IconButton";
import { DataGrid } from "~/ui/DataGrid/DataGrid";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";

import { EmptyState } from "./EmptyState";
import { useCreateApiKey } from "./hooks";

import type { ApiKeySectionAdminDisableApiKeyMutation$data } from "~/__generated__/ApiKeySectionAdminDisableApiKeyMutation.graphql";
import type { ApiKeySectionAdminEnableApiKeyMutation$data } from "~/__generated__/ApiKeySectionAdminEnableApiKeyMutation.graphql";
import type { ApiKeySectionDeleteApiKeyMutation } from "~/__generated__/ApiKeySectionDeleteApiKeyMutation.graphql";
import type { ApiKeySectionDisableApiKeyMutation$data } from "~/__generated__/ApiKeySectionDisableApiKeyMutation.graphql";
import type { ApiKeySectionEnableApiKeyMutation$data } from "~/__generated__/ApiKeySectionEnableApiKeyMutation.graphql";

import styles from "../css/DeveloperApi.module.css";

const disableApiKeyMutation = graphql`
	mutation ApiKeySectionDisableApiKeyMutation($id: ID!) {
		disableApiKey(id: $id) {
			disabledApiKeyId
		}
	}
`;

const adminDisableApiKeyMutation = graphql`
	mutation ApiKeySectionAdminDisableApiKeyMutation($id: ID!) {
		adminDisableApiKey(id: $id) {
			disabledApiKeyId
		}
	}
`;

const deleteApiKeyMutation = graphql`
	mutation ApiKeySectionDeleteApiKeyMutation($id: ID!) {
		deleteApiKey(id: $id) {
			deletedApiKeyId
		}
	}
`;

const adminEnableApiKeyMutation = graphql`
	mutation ApiKeySectionAdminEnableApiKeyMutation($id: ID!) {
		adminEnableApiKey(id: $id) {
			enabledApiKeyId
		}
	}
`;

const enableApiKeyMutation = graphql`
	mutation ApiKeySectionEnableApiKeyMutation($id: ID!) {
		enableApiKey(id: $id) {
			enabledApiKeyId
		}
	}
`;

export type ApiKey = Readonly<{
	id: string;
	name: string;
	createdAt: string | null;
	lastUsed: string | null;
	isActive: boolean;
	owner: {
		id: string;
		email: string;
	};
}>;

const columnHelper = createColumnHelper<ApiKey>();

const getDate = (date: string | null, fallback: string) => {
	if (!date) return fallback;
	return new Date(date).toLocaleDateString("en", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

interface ApiKeyMenuProps {
	apiKey: ApiKey;
	refetch: () => void;
}

const ApiKeyMenu = ({ apiKey, refetch }: ApiKeyMenuProps) => {
	const { user } = React.useContext(AppContext);
	const isAdmin = React.useMemo(() => user?.hasAdminAccess ?? false, [user?.hasAdminAccess]);

	const disableApiKey = React.useCallback(async () => {
		const confirmText = (
			<p>
				Are you sure you want to disable the API key <strong>{apiKey.name}</strong>? You can re-enable it at any time.
			</p>
		);
		if (
			!(await confirm(confirmText, {
				title: "Disable API Key",
				yesText: "Disable",
				noText: "Cancel",
				yesProps: { variant: "destructive" },
			}))
		) {
			return;
		}

		const mutation = isAdmin ? adminDisableApiKeyMutation : disableApiKeyMutation;

		commitMutation(environment, {
			mutation,
			variables: { id: apiKey.id },
			onCompleted: (response: {}) => {
				const typedResponse = response as
					| ApiKeySectionDisableApiKeyMutation$data
					| ApiKeySectionAdminDisableApiKeyMutation$data;
				const disabledId =
					(typedResponse as ApiKeySectionAdminDisableApiKeyMutation$data).adminDisableApiKey?.disabledApiKeyId ||
					(typedResponse as ApiKeySectionDisableApiKeyMutation$data).disableApiKey?.disabledApiKeyId;
				if (disabledId) {
					toast.success(`API key "${apiKey.name}" disabled successfully`);
					refetch();
				} else {
					toast.error(`Unable to disable API key "${apiKey.name}"`);
				}
			},
			onError: () => {
				toast.error(`Unable to disable API key "${apiKey.name}"`);
			},
		});
	}, [apiKey.id, apiKey.name, refetch, isAdmin]);

	const enableApiKey = React.useCallback(async () => {
		const mutation = isAdmin ? adminEnableApiKeyMutation : enableApiKeyMutation;

		commitMutation(environment, {
			mutation,
			variables: { id: apiKey.id },
			onCompleted: (response: {}) => {
				const typedResponse = response as
					| ApiKeySectionEnableApiKeyMutation$data
					| ApiKeySectionAdminEnableApiKeyMutation$data;
				const enabledId =
					(typedResponse as ApiKeySectionAdminEnableApiKeyMutation$data).adminEnableApiKey?.enabledApiKeyId ||
					(typedResponse as ApiKeySectionEnableApiKeyMutation$data).enableApiKey?.enabledApiKeyId;
				if (enabledId) {
					toast.success(`API key "${apiKey.name}" enabled successfully`);
					refetch();
				} else {
					toast.error(`Unable to enable API key "${apiKey.name}"`);
				}
			},
			onError: () => {
				toast.error(`Unable to enable API key "${apiKey.name}"`);
			},
		});
	}, [apiKey.id, apiKey.name, refetch, isAdmin]);

	const deleteApiKey = React.useCallback(async () => {
		const confirmText = (
			<p>
				Are you sure you want to delete the API key <strong>{apiKey.name}</strong>? Once deleted, it can’t be recovered.
			</p>
		);
		if (
			!(await confirm(confirmText, {
				title: "Delete API Key",
				yesText: "Delete",
				noText: "Cancel",
				yesProps: { variant: "destructive" },
			}))
		) {
			return;
		}
		commitMutation<ApiKeySectionDeleteApiKeyMutation>(environment, {
			mutation: deleteApiKeyMutation,
			variables: { id: apiKey.id },
			onCompleted: response => {
				if (response.deleteApiKey?.deletedApiKeyId) {
					toast.success(`API key "${apiKey.name}" deleted successfully`);
					refetch();
				} else {
					toast.error(`Unable to delete API key "${apiKey.name}"`);
				}
			},
			onError: () => {
				toast.error(`Unable to delete API key "${apiKey.name}"`);
			},
		});
	}, [apiKey.id, apiKey.name, refetch]);
	return (
		<Menu>
			<Menu.Trigger>
				<IconButton variant="tertiary" icon={faEllipsisV} className={styles.apiKeyMenuButton} />
			</Menu.Trigger>
			<Menu.Content align="end">
				<Menu.Item icon={faPowerOff} onSelect={apiKey.isActive ? disableApiKey : enableApiKey}>
					{apiKey.isActive ? "Disable key" : "Enable key"}
				</Menu.Item>
				<Menu.Separator />
				<Menu.Item icon={faTrashCan} destructive onSelect={deleteApiKey}>
					Delete key
				</Menu.Item>
			</Menu.Content>
		</Menu>
	);
};

interface ApiKeyTableProps {
	apiKeys: ApiKey[];
	emptyStateTitle: string;
	emptyStateDescription: string;
	showOwner?: boolean;
	showNewKeyButton: boolean;
	refetch: () => void;
	privileged?: boolean;
}

export const ApiKeyTable = ({
	apiKeys,
	emptyStateTitle,
	emptyStateDescription,
	showOwner = true,
	showNewKeyButton,
	refetch,
	privileged,
}: ApiKeyTableProps) => {
	const { createKey } = useCreateApiKey(privileged, refetch);
	const tableData = React.useMemo(() => Array.from(apiKeys), [apiKeys]);

	const columns = React.useMemo(
		() => [
			columnHelper.accessor("name", {
				header: "Name",
				cell: context => <span className={styles.boldedCell}>{context.getValue()}</span>,
				enableSorting: false,
			}),
			...(showOwner
				? [
						columnHelper.accessor(row => row.owner.email, {
							header: "Owner",
							cell: context => <span>{context.getValue()}</span>,
							enableSorting: false,
						}),
					]
				: []),
			columnHelper.accessor(row => row.isActive, {
				header: "Status",
				cell: context => (
					<span className={context.getValue() ? styles.statusActive : styles.statusInactive}>
						{context.getValue() ? "Active" : "Inactive"}
					</span>
				),
				enableSorting: false,
			}),
			columnHelper.accessor(row => row.createdAt, {
				header: "Created",
				cell: context => <span>{getDate(context.getValue(), "-")}</span>,
				enableSorting: false,
			}),
			columnHelper.accessor(row => row.lastUsed, {
				header: "Last used",
				cell: context => <span>{getDate(context.getValue(), "Never")}</span>,
				enableSorting: false,
			}),
			columnHelper.display({
				id: "actions",
				cell: context => <ApiKeyMenu apiKey={context.row.original} refetch={refetch} />,
			}),
		],
		[showOwner, refetch],
	);

	const table = useReactTable<ApiKey>({
		data: tableData,
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
	});

	if (apiKeys.length === 0) {
		return (
			<EmptyState
				title={emptyStateTitle}
				description={emptyStateDescription}
				onNewKey={showNewKeyButton ? createKey : undefined}
			/>
		);
	}
	return (
		<div className={styles.developerApiSettingsTableContainer}>
			{showNewKeyButton && (
				<div className={styles.developerApiSettingsTableContainerRow}>
					<Button variant="primary" onClick={createKey}>
						Create new API key
					</Button>
				</div>
			)}
			<DataGrid table={table} emptyStateMessage={emptyStateTitle} />
		</div>
	);
};

interface ApiKeySectionContentProps<TData> {
	query: GraphQLTaggedNode;
	dataSelector: (data: TData) => ApiKey[];
	emptyStateTitle: string;
	emptyStateDescription: string;
	fetchPolicy?: "store-or-network" | "network-only" | "store-and-network";
	fetchKey?: number;
	showOwner?: boolean;
	showNewKeyButton: boolean;
	privileged?: boolean;
}

const ApiKeySectionContent = <TQuery extends OperationType, TData>({
	query,
	dataSelector,
	emptyStateTitle,
	emptyStateDescription,
	showOwner = true,
	showNewKeyButton,
	privileged,
}: ApiKeySectionContentProps<TData>) => {
	const [refetchKey, setRefetchKey] = React.useState(0);

	const refetch = React.useCallback(() => {
		setRefetchKey(prev => prev + 1);
	}, []);

	const data = useLazyLoadQuery<TQuery>(query, {}, { fetchPolicy: "store-and-network", fetchKey: refetchKey });
	const apiKeys = dataSelector(data as TData);
	return (
		<ApiKeyTable
			apiKeys={apiKeys}
			emptyStateTitle={emptyStateTitle}
			emptyStateDescription={emptyStateDescription}
			showOwner={showOwner}
			showNewKeyButton={showNewKeyButton}
			refetch={refetch}
			privileged={privileged}
		/>
	);
};

interface ApiKeySectionProps<TData> {
	query: GraphQLTaggedNode;
	dataSelector: (data: TData) => ApiKey[];
	sectionTitle: string;
	enabled: boolean;
	emptyStateTitle: string;
	emptyStateDescription: string;
	emptyStateButton?: React.ReactNode;
	showOwner?: boolean;
	className?: string;
	showNewKeyButton?: boolean;
	privileged?: boolean;
}
export const ApiKeySection = <TQuery extends OperationType, TData>({
	query,
	dataSelector,
	sectionTitle,
	enabled,
	emptyStateTitle,
	emptyStateDescription,
	showOwner = true,
	className,
	showNewKeyButton = false,
	privileged,
}: ApiKeySectionProps<TData>) => {
	const sectionBody = React.useMemo(() => {
		const loader = <LoadingSpinner inline="centered" />;
		if (!enabled) return loader;
		// we only want to fetch the api keys when we know the developer api is enabled on the server
		// this is to avoid race conditions when the user toggles the developer api setting
		return (
			<React.Suspense fallback={loader}>
				<ApiKeySectionContent<TQuery, TData>
					query={query}
					dataSelector={dataSelector}
					emptyStateTitle={emptyStateTitle}
					emptyStateDescription={emptyStateDescription}
					showOwner={showOwner}
					showNewKeyButton={showNewKeyButton}
					privileged={privileged}
				/>
			</React.Suspense>
		);
		// eslint-disable-next-line max-len
	}, [enabled, query, dataSelector, emptyStateTitle, emptyStateDescription, showOwner, showNewKeyButton, privileged]);

	return (
		<div className={cx(styles.developerApiSettingsSection, className)}>
			<div className={styles.developerApiSettingsLabel}>{sectionTitle}</div>
			{sectionBody}
		</div>
	);
};
